import { groupMessagesByKey } from './groupMessagesByKey';

export const ROUTING_WARNING_TYPE = {
    GEOCODE_NOT_FOUND: 'geocodeNotFound',
    OUTSIDE_RADIUS: 'outsideRadius',
    OFF_TRACK: 'offTrack',
    RISK_ZONE: 'riskZone',
    IGNORED_STOPS: 'ignoredStops',
    PASSENGERS_WITHOUT_SHIFT: 'passengersWithoutShift',
    REJECTED_PASSENGERS: 'rejectedPassengers',
    CERCA_COORDS_NOT_FOUND: 'cercaCoordsNotFound',
    PREVIEW_AREA: 'previewArea',
    NO_ROUTES_TO_SAVE: 'noRoutesToSave',
    NO_VALID_ROUTES_FOR_LINES: 'noValidRoutesForLines',
    FLEET_OVER_CAPACITY: 'fleetOverCapacity',
    FLEET_UNDERUSED: 'fleetUnderused',
    CLUSTER_TYPE: 'clusterType',
    GROUP_ROUTING_FAILURE: 'groupRoutingFailure',
    UNALLOCATED: 'unallocated',
    EMPLOYEE_SHIFT: 'employeeShift',
    OTHER: 'other',
};

export const ROUTING_WARNING_TYPE_ORDER = [
    ROUTING_WARNING_TYPE.GEOCODE_NOT_FOUND,
    ROUTING_WARNING_TYPE.OUTSIDE_RADIUS,
    ROUTING_WARNING_TYPE.PASSENGERS_WITHOUT_SHIFT,
    ROUTING_WARNING_TYPE.REJECTED_PASSENGERS,
    ROUTING_WARNING_TYPE.CERCA_COORDS_NOT_FOUND,
    ROUTING_WARNING_TYPE.CLUSTER_TYPE,
    ROUTING_WARNING_TYPE.FLEET_OVER_CAPACITY,
    ROUTING_WARNING_TYPE.FLEET_UNDERUSED,
    ROUTING_WARNING_TYPE.GROUP_ROUTING_FAILURE,
    ROUTING_WARNING_TYPE.UNALLOCATED,
    ROUTING_WARNING_TYPE.OFF_TRACK,
    ROUTING_WARNING_TYPE.RISK_ZONE,
    ROUTING_WARNING_TYPE.EMPLOYEE_SHIFT,
    ROUTING_WARNING_TYPE.IGNORED_STOPS,
    ROUTING_WARNING_TYPE.PREVIEW_AREA,
    ROUTING_WARNING_TYPE.NO_ROUTES_TO_SAVE,
    ROUTING_WARNING_TYPE.NO_VALID_ROUTES_FOR_LINES,
    ROUTING_WARNING_TYPE.OTHER,
];

export function normalizeRoutingWarning(entry) {
    if (!entry) return null;
    if (typeof entry === 'string') {
        return { type: ROUTING_WARNING_TYPE.OTHER, message: entry };
    }
    if (typeof entry === 'object' && entry.message) {
        return {
            type: entry.type || ROUTING_WARNING_TYPE.OTHER,
            message: entry.message,
        };
    }
    return null;
}

export function groupRoutingWarningsByType(warnings) {
    return groupMessagesByKey(warnings, {
        getKey: (entry) => normalizeRoutingWarning(entry)?.type ?? ROUTING_WARNING_TYPE.OTHER,
        getMessage: (entry) => normalizeRoutingWarning(entry)?.message,
        order: ROUTING_WARNING_TYPE_ORDER,
    }).map(({ key, messages }) => ({ type: key, messages }));
}

/** Avisos de exemplo para testar o diálogo paginado (dev/QA). */
export function buildSamplePostRoutingWarnings(t, { offTrackDistanceM = 20 } = {}) {
    const reasonMaxTrip = t('routePlanning.validation.groupRoutingFailedReasonMaxTrip');
    const reasonFleet = t('routePlanning.validation.groupRoutingFailedReasonInsufficientFleet');
    return [
        {
            type: ROUTING_WARNING_TYPE.GEOCODE_NOT_FOUND,
            message: t('routePlanning.messages.geocodeAddressesNotFound', { count: 2 }),
        },
        {
            type: ROUTING_WARNING_TYPE.OUTSIDE_RADIUS,
            message: t('routePlanning.messages.outsideDestinationRadiusWarning', { count: 1 }),
        },
        {
            type: ROUTING_WARNING_TYPE.PASSENGERS_WITHOUT_SHIFT,
            message: t('routePlanning.messages.passengersWithoutShift', { count: 2 }),
        },
        {
            type: ROUTING_WARNING_TYPE.CLUSTER_TYPE,
            message: t('routePlanning.validation.clusterTypeUsed', {
                clusterType: t('routePlanning.filter.clusterIdealRadius'),
            }),
        },
        {
            type: ROUTING_WARNING_TYPE.FLEET_UNDERUSED,
            message: t('routePlanning.validation.notAllVehiclesUsed', { used: 2, total: 4 }),
        },
        {
            type: ROUTING_WARNING_TYPE.GROUP_ROUTING_FAILURE,
            message: t('routePlanning.validation.groupRoutingFailedIda', {
                time: '07:30',
                reasonSuffix: reasonMaxTrip,
            }),
        },
        {
            type: ROUTING_WARNING_TYPE.GROUP_ROUTING_FAILURE,
            message: t('routePlanning.validation.groupRoutingFailedVolta', {
                time: '18:00',
                reasonSuffix: reasonFleet,
            }),
        },
        {
            type: ROUTING_WARNING_TYPE.UNALLOCATED,
            message: t('routePlanning.messages.unallocatedPassengersWarning', { count: 5 }),
        },
        {
            type: ROUTING_WARNING_TYPE.OFF_TRACK,
            message: t('routePlanning.messages.offTrackStopsWarning', {
                count: 3,
                distance: offTrackDistanceM,
            }),
        },
        {
            type: ROUTING_WARNING_TYPE.EMPLOYEE_SHIFT,
            message: t('routePlanning.warnings.employeeShiftUnallocated'),
        },
        {
            type: ROUTING_WARNING_TYPE.EMPLOYEE_SHIFT,
            message: t('routePlanning.warnings.employeeShiftUnallocatedLine', {
                routes: '1, 2',
                departure: '06:00',
                arrival: '22:30',
            }),
        },
    ];
}

const DEV_SIM_GLOBAL = '__merustrackerAggregatedWarningsSim';

/** Expõe simulação no console do navegador (somente quando o caller invoca em ambiente dev). */
export function mountAggregatedWarningsDevSim(openWarnings, t, options = {}) {
    if (typeof window === 'undefined') return () => {};
    window[DEV_SIM_GLOBAL] = {
        all: () => openWarnings(buildSamplePostRoutingWarnings(t, options)),
        open: (warnings) => openWarnings(warnings),
    };
    return () => {
        delete window[DEV_SIM_GLOBAL];
    };
}
